import clsx from "clsx";
import { format, parseISO } from "date-fns";
import PropTypes from "prop-types";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  ClickAwayListener,
  Popover,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Checkbox from "../../common/FormControl/Checkbox";
import IconLink from "../../common/FormControl/IconLink";
import ColumnSearch from "./ColumnSearch";
import { headerFilterFields, initialToggleHeader } from "./helper";
import SortPopover from "./SortPopover";

const useStyles = makeStyles(theme => ({
  tableWrap: {
    overflowX: "auto",
    backgroundColor: theme.palette.common.white,
    "& tr:last-child td": {
      borderBottom: 0,
    },
  },
  th: {
    // color: theme.palette.primary.main,
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    padding: "4px 5px 2px 5px",
    whiteSpace: "noWrap",
    verticalAlign: "text-top",
  },
  thName: {
    padding: "4px 5px 2px 0",
    width: "23%",
  },
  thCheckbox: {
    padding: "4px 5px 2px 10px",
    width: 28,
  },
  thSort: {
    minWidth: 90,
  },
  thDesc: {
    minWidth: 200,
  },
  iconButtonSort: {
    padding: "2px 4px",
    color: theme.palette.common.link,
    marginLeft: 5,
  },
  icon: {
    width: "unset",
    height: "unset",
    overflow: "unset",
    fontSize: 12,
  },
  paperPopover: {
    padding: 10,
    boxShadow: "0px 3px 12px -2px rgba(0, 0, 0, 0.24)",
    minWidth: 150,
  },
  thActions: {
    minWidth: "28px",
  },
  activeFilter: {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
}));

const ProductsTableHeader = ({
  ascending,
  disableColumnHeaderInteraction = false,
  fileViewFields,
  handleSelectAllResources,
  hideSelectAll = false,
  searchValues,
  selectAll,
  setAscending,
  setSearchValues,
  setSortfield,
  sortfield,
}) => {
  const classes = useStyles();

  const [columnFilterValues, setColumnFilterValues] = useState({
    sortfield: "",
    value: "",
    date: null,
  });

  const [toggleHeader, setToggleHeader] = useState(initialToggleHeader);

  const isDateFilter = sortfield =>
    ["UploadedDate", "DownloadedDate", "ChangedDate"].includes(sortfield);

  const handleToggleHeader = name => {
    const isOpening = !toggleHeader[name];
    const filterField = headerFilterFields[name];

    if (isOpening && searchValues?.sortfield === filterField) {
      const isDate = isDateFilter(filterField);

      setColumnFilterValues({
        sortfield: filterField,
        value: isDate ? "" : searchValues.value,
        date: isDate ? parseISO(searchValues.value) : null,
      });
    } else {
      setColumnFilterValues({
        sortfield: "",
        value: "",
        date: null,
      });
    }

    setToggleHeader({
      ...initialToggleHeader,
      [name]: isOpening,
    });
  };

  const [anchorElSortUpload, setAnchorElSortUpload] = useState(null);
  const handleClickSortUpload = event => {
    setAnchorElSortUpload(event.currentTarget);
  };
  const handleCloseSortUpload = () => {
    setAnchorElSortUpload(null);
  };
  const openSortUpload = Boolean(anchorElSortUpload);

  const [anchorElSortDownload, setAnchorElSortDownload] = useState(null);
  const handleClickSortDownload = event => {
    setAnchorElSortDownload(event.currentTarget);
  };
  const handleCloseSortDownload = () => {
    setAnchorElSortDownload(null);
  };
  const openSortDownload = Boolean(anchorElSortDownload);

  const [anchorElSortEdited, setAnchorElSortEdited] = useState(null);
  const handleClickSortEdited = event => {
    setAnchorElSortEdited(event.currentTarget);
  };
  const handleCloseSortEdited = () => {
    setAnchorElSortEdited(null);
  };
  const openSortEdited = Boolean(anchorElSortEdited);

  const handleRequestSort = (event, property) => {
    const isDesc = sortfield === property && ascending === false;

    setSortfield(property);
    setAscending(isDesc);
  };

  const handleSortField = property => event => {
    handleRequestSort(event, property);
  };

  const handleCloseHeader = () => {
    setToggleHeader(initialToggleHeader);
  };

  const handleColumnFilter = e => {
    const { name, value } = e.target;

    setColumnFilterValues({
      sortfield: name,
      value,
      date: null,
    });
  };

  const handleSearchValues = () => {
    if (columnFilterValues.date) {
      setSearchValues({
        sortfield: columnFilterValues.sortfield,
        value: format(columnFilterValues.date, "yyyy-MM-dd"),
      });

      return;
    }

    if (columnFilterValues.sortfield && columnFilterValues.value) {
      setSearchValues({
        sortfield: columnFilterValues.sortfield,
        value: columnFilterValues.value,
      });

      return;
    }

    setSearchValues(null);
  };

  const handleClear = () => {
    setColumnFilterValues({
      sortfield: "",
      value: "",
      date: null,
    });
  };

  const handleColumnFilterByDate = (name, date) => {
    setColumnFilterValues({
      sortfield: name,
      value: "",
      date,
    });
  };

  const isChanged = sortfield === "ChangedBy" || sortfield === "ChangedDate";
  const isDownloaded = sortfield === "DownloadedBy" || sortfield === "DownloadedDate";
  const isUploaded = sortfield === "UploadedBy" || sortfield === "UploadedDate";

  const isColumnSorted = name => sortfield === name;

  const isColumnFiltered = name =>
    searchValues?.sortfield === name &&
    Boolean(searchValues?.value);

  const isGroupFiltered = fields =>
    fields.includes(searchValues?.sortfield);

  const isUploadedFiltered = isGroupFiltered(["UploadedBy", "UploadedDate"]);
  const isDownloadedFiltered = isGroupFiltered(["DownloadedBy", "DownloadedDate"]);
  const isChangedFiltered = isGroupFiltered(["ChangedBy", "ChangedDate"]);

  return (
    <TableHead>
      <TableRow>
        {!hideSelectAll && (
          <TableCell className={clsx([classes.th, classes.thCheckbox])}>
            <Checkbox
              onChange={e => handleSelectAllResources(e)}
              value={selectAll}
            />
          </TableCell>
        )}
        <TableCell className={classes.th} />

        {fileViewFields.nameColumn && (
          <TableCell className={clsx([classes.th, classes.thName])}>
            {disableColumnHeaderInteraction ? (
              <FormattedMessage id="product.name" />
            ) : (
              <>
                <IconLink
                  icon="fa fa-search"
                  customClass={clsx(classes.colSearchIcon, {
                    [classes.activeFilter]: isColumnFiltered("Name"),
                  })}
                  onClick={() => handleToggleHeader("name")}
                />
                <TableSortLabel
                  active={isColumnSorted("Name")}
                  direction={isColumnSorted("Name") && ascending ? "asc" : "desc"}
                  onClick={handleSortField("Name")}
                >
                  <FormattedMessage id="product.name" />
                </TableSortLabel>
                {toggleHeader.name && (
                  <ClickAwayListener onClickAway={handleCloseHeader}>
                    <div>
                      <ColumnSearch
                        name="Name"
                        value={columnFilterValues.value}
                        onChange={handleColumnFilter}
                        onClear={handleClear}
                        onSearch={handleSearchValues}
                      />
                    </div>
                  </ClickAwayListener>
                )}
              </>
            )}
          </TableCell>
        )}

        {fileViewFields.descriptionColumn && (
          <TableCell className={clsx([classes.th, classes.thDesc])}>
            {disableColumnHeaderInteraction ? (
              <FormattedMessage id="product.description" />
            ) : (
              <>
                <IconLink
                  icon="fa fa-search"
                  customClass={clsx(classes.colSearchIcon, {
                    [classes.activeFilter]: isColumnFiltered("Description"),
                  })}
                  onClick={() => handleToggleHeader("description")}
                />
                <TableSortLabel
                  active={isColumnSorted("Description")}
                  direction={isColumnSorted("Description") && ascending ? "asc" : "desc"}
                  onClick={handleSortField("Description")}
                >
                  <FormattedMessage id="product.description" />
                </TableSortLabel>
                {toggleHeader.description && (
                  <ClickAwayListener onClickAway={handleCloseHeader}>
                    <div>
                      <ColumnSearch
                        name="Description"
                        value={columnFilterValues.value}
                        onChange={handleColumnFilter}
                        onClear={handleClear}
                        onSearch={handleSearchValues}
                      />
                    </div>
                  </ClickAwayListener>
                )}
              </>
            )}
          </TableCell>
        )}

        {fileViewFields.uploadedColumn && (
          <TableCell className={clsx([classes.th, classes.thSort])}>
            {disableColumnHeaderInteraction ? (
              <FormattedMessage id="product.uploaded" />
            ) : (
              <>
                <IconLink
                  icon="fa fa-search"
                  customClass={clsx(classes.colSearchIcon, {
                    [classes.activeFilter]: isUploadedFiltered,
                  })}
                  onClick={handleClickSortUpload}
                />

                <TableSortLabel
                  onClick={handleClickSortUpload}
                  active={isUploaded}
                  direction={isUploaded && ascending ? "asc" : "desc"}
                >
                  <FormattedMessage id="product.uploaded" />
                </TableSortLabel>

                <Popover
                  open={openSortUpload}
                  anchorEl={anchorElSortUpload}
                  onClose={handleCloseSortUpload}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  classes={{
                    root: classes.rootPopover,
                    paper: classes.paperPopover,
                  }}
                >
                  <SortPopover
                    name="Uploaded"
                    sortfield={sortfield}
                    ascending={ascending}
                    columnFilterValues={columnFilterValues}
                    searchValues={searchValues}
                    toggleHeader={toggleHeader}
                    onClear={handleClear}
                    onCloseHeader={handleCloseHeader}
                    onColumnFilter={handleColumnFilter}
                    onDateChange={handleColumnFilterByDate}
                    onSearch={handleSearchValues}
                    onSortField={handleSortField}
                    onToggleHeader={handleToggleHeader}
                  />
                </Popover>
              </>
            )}
          </TableCell>
        )}

        {fileViewFields.downloadedColumn && (
          <TableCell className={clsx([classes.th, classes.thSort])}>
            {disableColumnHeaderInteraction ? (
              <FormattedMessage id="product.downloaded" />
            ) : (
              <>
                <IconLink
                  icon="fa fa-search"
                  customClass={clsx(classes.colSearchIcon, {
                    [classes.activeFilter]: isDownloadedFiltered,
                  })}
                  onClick={handleClickSortDownload}
                />

                <TableSortLabel
                  onClick={handleClickSortDownload}
                  active={isDownloaded}
                  direction={isDownloaded && ascending ? "asc" : "desc"}
                >
                  <FormattedMessage id="product.downloaded" />
                </TableSortLabel>

                <Popover
                  open={openSortDownload}
                  anchorEl={anchorElSortDownload}
                  onClose={handleCloseSortDownload}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  classes={{
                    root: classes.rootPopover,
                    paper: classes.paperPopover,
                  }}
                >
                  <SortPopover
                    name="Downloaded"
                    sortfield={sortfield}
                    ascending={ascending}
                    columnFilterValues={columnFilterValues}
                    searchValues={searchValues}
                    toggleHeader={toggleHeader}
                    onClear={handleClear}
                    onCloseHeader={handleCloseHeader}
                    onColumnFilter={handleColumnFilter}
                    onDateChange={handleColumnFilterByDate}
                    onSearch={handleSearchValues}
                    onSortField={handleSortField}
                    onToggleHeader={handleToggleHeader}
                  />
                </Popover>
              </>
            )}
          </TableCell>
        )}

        {fileViewFields.editedColumn && (
          <TableCell className={clsx([classes.th, classes.thSort])}>
            {disableColumnHeaderInteraction ? (
              <FormattedMessage id="product.edited" />
            ) : (
              <>
                <IconLink
                  icon="fa fa-search"
                  customClass={clsx(classes.colSearchIcon, {
                    [classes.activeFilter]: isChangedFiltered,
                  })}
                  onClick={handleClickSortEdited}
                />

                <TableSortLabel
                  onClick={handleClickSortEdited}
                  active={isChanged}
                  direction={isChanged && ascending ? "asc" : "desc"}
                >
                  <FormattedMessage id="product.edited" />
                </TableSortLabel>
                <Popover
                  open={openSortEdited}
                  anchorEl={anchorElSortEdited}
                  onClose={handleCloseSortEdited}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  classes={{
                    root: classes.rootPopover,
                    paper: classes.paperPopover,
                  }}
                >
                  <SortPopover
                    name="Changed"
                    sortfield={sortfield}
                    ascending={ascending}
                    columnFilterValues={columnFilterValues}
                    searchValues={searchValues}
                    toggleHeader={toggleHeader}
                    onClear={handleClear}
                    onCloseHeader={handleCloseHeader}
                    onColumnFilter={handleColumnFilter}
                    onDateChange={handleColumnFilterByDate}
                    onSearch={handleSearchValues}
                    onSortField={handleSortField}
                    onToggleHeader={handleToggleHeader}
                  />
                </Popover>
              </>
            )}
          </TableCell>
        )}

        {fileViewFields.versionColumn && (
          <TableCell className={classes.th} align="center">
            {disableColumnHeaderInteraction ? (
              <FormattedMessage id="product.version" />
            ) : (
              <>
                <IconLink
                  icon="fa fa-search"
                  customClass={clsx(classes.colSearchIcon, {
                    [classes.activeFilter]: isColumnFiltered("VersionNo"),
                  })}
                  onClick={() => handleToggleHeader("version")}
                />
                <TableSortLabel
                  active={isColumnSorted("VersionNo")}
                  direction={isColumnSorted("VersionNo") && ascending ? "asc" : "desc"}
                  onClick={handleSortField("VersionNo")}
                >
                  <FormattedMessage id="product.version" />
                </TableSortLabel>
                {toggleHeader.version && (
                  <ClickAwayListener onClickAway={handleCloseHeader}>
                    <div>
                      <ColumnSearch
                        name="VersionNo"
                        value={columnFilterValues.value}
                        onChange={handleColumnFilter}
                        onClear={handleClear}
                        onSearch={handleSearchValues}
                      />
                    </div>
                  </ClickAwayListener>
                )}
              </>
            )}
          </TableCell>
        )}

        {fileViewFields.typeColumn && (
          <TableCell className={classes.th} align="center">
            {disableColumnHeaderInteraction ? (
              <FormattedMessage id="product.type" />
            ) : (
              <>
                <IconLink
                  icon="fa fa-search"
                  customClass={clsx(classes.colSearchIcon, {
                    [classes.activeFilter]: isColumnFiltered("Type"),
                  })}
                  onClick={() => handleToggleHeader("type")}
                />
                <TableSortLabel
                  active={isColumnSorted("Type")}
                  direction={isColumnSorted("Type") && ascending ? "asc" : "desc"}
                  onClick={handleSortField("Type")}
                >
                  <FormattedMessage id="product.type" />
                </TableSortLabel>
                {toggleHeader.type && (
                  <ClickAwayListener onClickAway={handleCloseHeader}>
                    <div>
                      <ColumnSearch
                        name="Type"
                        value={columnFilterValues.value}
                        onChange={handleColumnFilter}
                        onClear={handleClear}
                        onSearch={handleSearchValues}
                      />
                    </div>
                  </ClickAwayListener>
                )}
              </>
            )}
          </TableCell>
        )}

        {fileViewFields.sizeColumn && (
          <TableCell className={classes.th}>
            {disableColumnHeaderInteraction ? (
              <FormattedMessage id="product.size" />
            ) : (
              <>
                <IconLink
                  icon="fa fa-search"
                  customClass={clsx(classes.colSearchIcon, {
                    [classes.activeFilter]: isColumnFiltered("Size"),
                  })}
                  onClick={() => handleToggleHeader("size")}
                />
                <TableSortLabel
                  active={isColumnSorted("Size")}
                  direction={isColumnSorted("Size") && ascending ? "asc" : "desc"}
                  onClick={handleSortField("Size")}
                >
                  <FormattedMessage id="product.size" />
                </TableSortLabel>
                {toggleHeader.size && (
                  <ClickAwayListener onClickAway={handleCloseHeader}>
                    <div>
                      <ColumnSearch
                        name="Size"
                        value={columnFilterValues.value}
                        onChange={handleColumnFilter}
                        onClear={handleClear}
                        onSearch={handleSearchValues}
                      />
                    </div>
                  </ClickAwayListener>
                )}
              </>
            )}
          </TableCell>
        )}

        <TableCell
          className={clsx([classes.th, classes.thActions])}
          align="right"
          onClick={
            disableColumnHeaderInteraction ? undefined : () => setSortfield("Locked")
          }
        />
      </TableRow>
    </TableHead>
  );
};

ProductsTableHeader.propTypes = {
  ascending: PropTypes.bool.isRequired,
  disableColumnHeaderInteraction: PropTypes.bool,
  fileViewFields: PropTypes.shape({
    nameColumn: PropTypes.bool,
    descriptionColumn: PropTypes.bool,
    uploadedColumn: PropTypes.bool,
    downloadedColumn: PropTypes.bool,
    editedColumn: PropTypes.bool,
    versionColumn: PropTypes.bool,
    typeColumn: PropTypes.bool,
    sizeColumn: PropTypes.bool,
  }).isRequired,
  handleSelectAllResources: PropTypes.func.isRequired,
  hideSelectAll: PropTypes.bool,
  searchValues: PropTypes.shape({
    sortfield: PropTypes.string,
    value: PropTypes.string,
  }),
  selectAll: PropTypes.bool.isRequired,
  setAscending: PropTypes.func.isRequired,
  setSearchValues: PropTypes.func.isRequired,
  setSortfield: PropTypes.func.isRequired,
  sortfield: PropTypes.string.isRequired,
};

export default ProductsTableHeader;
